import { cn } from "@aurumiq/ui/lib/utils";
import type { ReactNode } from "react";

/** Inline `**bold**`, `` `code` `` and `[label](href)`; everything else is plain text. */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(re)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const tok = m[0];
    const key = `${idx}-${i++}`;
    if (tok.startsWith("**")) out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`"))
      out.push(
        <code key={key} className="rounded bg-muted px-1 font-mono text-[0.9em]">
          {tok.slice(1, -1)}
        </code>,
      );
    else {
      const [, label, href] = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok) ?? [];
      out.push(
        <a
          key={key}
          href={href}
          className="text-primary underline"
          target="_blank"
          rel="noreferrer"
        >
          {label}
        </a>,
      );
    }
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * Minimal, dependency-free markdown preview (headings, paragraphs, bullet / numbered lists,
 * fenced code). Good enough for release notes; not a full CommonMark renderer.
 */
export function Markdown({ source, className }: { source: string; className?: string }) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const nodes: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const key = `l${i}`;
    if (line.startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").startsWith("```")) {
        buf.push(lines[i] ?? "");
        i++;
      }
      i++;
      nodes.push(
        <pre key={key} className="overflow-auto rounded-md border bg-muted p-2 font-mono text-xs">
          {buf.join("\n")}
        </pre>,
      );
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1]?.length ?? 1;
      const Tag = `h${Math.min(level + 1, 6)}` as "h2" | "h3" | "h4" | "h5" | "h6";
      nodes.push(
        <Tag key={key} className={cn("font-semibold", level <= 1 ? "text-lg" : "text-base")}>
          {inline(h[2] ?? "")}
        </Tag>,
      );
      i++;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*(?:[-*]|\d+\.)\s+/.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^\s*(?:[-*]|\d+\.)\s+/, ""));
        i++;
      }
      const List = ordered ? "ol" : "ul";
      nodes.push(
        <List key={key} className={cn("pl-5", ordered ? "list-decimal" : "list-disc")}>
          {items.map((it, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static list
            <li key={j}>{inline(it)}</li>
          ))}
        </List>,
      );
      continue;
    }
    if (line.trim() === "") {
      i++;
      continue;
    }
    const buf: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? "").trim() !== "" &&
      !/^(#|```|\s*[-*]\s|\s*\d+\.\s)/.test(lines[i] ?? "")
    ) {
      buf.push(lines[i] ?? "");
      i++;
    }
    nodes.push(<p key={key}>{inline(buf.join(" "))}</p>);
  }
  return <div className={cn("grid gap-2 text-sm leading-relaxed", className)}>{nodes}</div>;
}
